import { useSelector } from "react-redux";
import Total from "./Total";
import { Form } from "./types";
import { yupResolver } from "@hookform/resolvers/yup";
import "./IncomeHandler.scss";
import {
  selectPricingAgreementsForClient,
  selectProducts,
} from "../../../../../ducks/data/products/selectors";
import { useL10n } from "../../../../../l10n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { DoraSelect, UFDoraSelect, DoraButton, UFDoraTextInput } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import { selectUnits } from "../../../../../ducks/data/finance/units/selectors";
import { useElementId } from "../../../../../hooks";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./InvoiceLineRowForm.scss";
import ValidationError from "./ValidationError";
import { TextField } from "@mui/material";
import {
  selectFinanceConfiguration,
  selectIsFinanceIntegrationPowerOffice,
  selectIsFinanceIntegrationTripletex,
} from "../../../../../ducks/data/finance/selectors";
import { selectProductsForDispatcherTeamProductGroups } from "../../../../../ducks/data/products/selectors";
import { useFeature } from "../../../../../hooks";
import { useAppDispatch } from "../../../../../redux-store";
import { invoiceLineDescriptionRemembered } from "../../../../../ducks/data/autocomplete-suggestions";
import { UFInvoiceLineDescriptionAutocomplete } from "../../../../Toolkit/DoraMultilineTextAutocomplete";

// Stable empty reference used when authoring pricing agreements, so the
// agreement-based auto-fill/relabel logic below is effectively switched off.
const EMPTY_PRICING_AGREEMENTS: ReturnType<
  ReturnType<typeof selectPricingAgreementsForClient>
> = [];

export const InvoiceLineRowForm = (props: {
  createdDate?: string;
  defaultValues: Form;
  onCancel: () => void;
  onSubmit: (form: Form) => Promise<void>;
  noCheckboxes: boolean;
  skipInvoiceLineSpecificCells?: boolean;
  showStatusColumn?: boolean;
  clientId?: string;
  extraError?: string;
  // When true, the form is being used to author the pricing agreements
  // themselves (customer page) rather than to add invoice lines. In that mode
  // it must ignore the client's existing pricing agreements and pre-fill from
  // the product's own defaults / show the real product name.
  isPricingAgreementConfig?: boolean;
}) => {
  const { t } = useTranslation(["components", "invoicing", "types"]);
  const isTripletex = useSelector(selectIsFinanceIntegrationTripletex);
  const isPowerOffice = useSelector(selectIsFinanceIntegrationPowerOffice);
  const {
    defaultValues,
    skipInvoiceLineSpecificCells,
    showStatusColumn,
    noCheckboxes,
    clientId,
    extraError,
    isPricingAgreementConfig,
    onSubmit,
    onCancel,
  } = props;
  const l10n = useL10n();
  const schema = useMemo(
    () =>
      yup.object({
        description: yup.string().nullable(),
        productId: yup.string().nullable(),
        unitId: yup.string().nullable(),
        quantity: yup
          .number()
          .required()
          .positive()
          .transform((_, strValue) => l10n.parseNumber(strValue)),
        amount: yup
          .number()
          .required()
          .transform((_, strValue: number) => Math.round(strValue * 100) / 100)
          .transform((_, strValue) => l10n.parseNumber(strValue)),
        discountPercentage: yup
          .number()
          .nullable()
          .transform((_, strValue) => {
            if (strValue === "" || strValue === "0") {
              return null;
            }
            const parsed = l10n.parseDecimal(strValue);
            return parsed ? parsed.toDecimalPlaces(2).toNumber() : NaN;
          })
          .min(0.01),
        // .max(99.99),
      }),
    [l10n]
  );

  const formMethods = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { register, handleSubmit, control, getValues } = formMethods;
  const errors = formMethods.formState.errors;
  const quantityValidationId = useElementId();
  const amountValidationId = useElementId();
  const isEconomicProductGroupsEnabled = useFeature("economic-product-groups");
  const financeConfig = useSelector(selectFinanceConfiguration);
  const productsGroupsEnabled = useMemo(
    () => financeConfig === "E-CONOMIC" && isEconomicProductGroupsEnabled,
    [financeConfig, isEconomicProductGroupsEnabled]
  );
  const products = useSelector(selectProducts);
  const dispatcherVisibleProducts = useSelector(
    selectProductsForDispatcherTeamProductGroups
  );
  const pricingAgreementsForClient = useSelector(
    selectPricingAgreementsForClient(clientId || "")
  );
  // When authoring the pricing agreements themselves, ignore existing agreements
  // so the form pre-fills from the product's own defaults and shows the real
  // product name instead of copying values from an existing agreement.
  const pricingAgreements = isPricingAgreementConfig
    ? EMPTY_PRICING_AGREEMENTS
    : pricingAgreementsForClient;
  const units = useSelector(selectUnits);
  const dispatch = useAppDispatch();
  // Pricing agreements are configured on the customer page, not typed per
  // line, so only real invoice lines use (and feed) the suggestions.
  const descriptionAutocompleteEnabled =
    useFeature("user-shipment-fields-autocomplete-from-server") &&
    !isPricingAgreementConfig;
  const selectedProductId = useWatch({ control, name: "productId" }) ?? null;

  // Same rule as the server: a description that is just the name the product
  // or one of its pricing agreements fills in is not worth suggesting.
  const isAutoFilledDescription = useCallback(
    (productId: string | null, description: string) =>
      !!productId &&
      [
        products.find((p) => p.id === productId)?.name,
        ...pricingAgreements
          .filter((pa) => pa.productId === productId)
          .map((pa) => pa.name),
      ].some((name) => name?.trim() === description),
    [products, pricingAgreements]
  );

  const inputValidationProps = useCallback(
    (id: string, field: keyof Form) => {
      const fieldError = formMethods.formState.errors[field];
      return fieldError ? { "aria-invalid": true, "aria-describedby": id } : {};
    },
    [formMethods.formState]
  );

  // Guards against the save button firing more than once for the same edit
  // (fast double-click, Enter auto-repeat, a duplicate submit event) — without
  // this, two identical requests can race the delivery's optimistic-concurrency
  // check and the loser comes back as a spurious 409.
  const isSubmittingRef = useRef(false);
  const [isSubmittingRow, setIsSubmittingRow] = useState(false);

  const submitRow = useMemo(() => {
    return handleSubmit(async (values) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      setIsSubmittingRow(true);
      try {
        await onSubmit(values);
        const description = values.description?.trim();
        const productId = values.productId ?? null;
        if (
          descriptionAutocompleteEnabled &&
          description &&
          !isAutoFilledDescription(productId, description)
        ) {
          dispatch(invoiceLineDescriptionRemembered({ productId, description }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        isSubmittingRef.current = false;
        setIsSubmittingRow(false);
      }
    });
  }, [
    handleSubmit,
    onSubmit,
    descriptionAutocompleteEnabled,
    isAutoFilledDescription,
    dispatch,
  ]);

  // Each option carries the underlying product id and (optionally) the specific
  // agreement it represents, so selecting it can pre-fill from that agreement
  // while still submitting the product id.
  type ProductSelectOption = {
    value: string;
    label: string;
    productId: string;
    agreement: (typeof pricingAgreements)[number] | null;
  };

  const buildOptions = useCallback(
    (productList: typeof products): ProductSelectOption[] =>
      productList.flatMap((product): ProductSelectOption[] => {
        const agreementsForProduct = pricingAgreements.filter(
          (pa) => pa.productId === product.id
        );
        // A product with pricing agreements is represented by one option per
        // agreement (no plain-product entry); a product without agreements keeps
        // its single product option.
        if (agreementsForProduct.length > 0) {
          return agreementsForProduct.map((pa) => ({
            value: pa.id,
            label: `${product.productNumber} ${pa.name || product.name}`,
            productId: product.id,
            agreement: pa,
          }));
        }
        return [
          {
            value: product.id,
            label: `${product.productNumber} ${product.name}`,
            productId: product.id,
            agreement: null,
          },
        ];
      }),
    [pricingAgreements]
  );

  const options = useMemo(
    () => buildOptions(products),
    [buildOptions, products]
  );

  const selectableOptions = useMemo(
    () =>
      productsGroupsEnabled ? buildOptions(dispatcherVisibleProducts) : options,
    [productsGroupsEnabled, dispatcherVisibleProducts, buildOptions, options]
  );

  const optionByValue = useMemo(() => {
    const map = new Map<string, ProductSelectOption>();
    for (const option of options) {
      map.set(option.value, option);
    }
    return map;
  }, [options]);

  const [selectedOptionValue, setSelectedOptionValue] = useState<
    string | undefined
  >(undefined);

  // Picking an option fills the row from the chosen agreement (or, for a
  // plain-product option, the product's own defaults) and records the underlying
  // product id on the form for submission.
  const handleProductOptionChange = useCallback(
    (value?: string | null) => {
      setSelectedOptionValue(value || undefined);
      const option = value ? optionByValue.get(value) : undefined;
      if (!option) {
        formMethods.setValue("productId", null);
        return;
      }
      const { agreement } = option;
      const product = products.find((p) => p.id === option.productId);
      formMethods.setValue("productId", option.productId);
      if (!product) {
        return;
      }
      const salesPrice = agreement?.salesPrice || product.salesPrice || "";
      if (salesPrice) {
        formMethods.setValue("amount", l10n.formatCurrency(salesPrice));
      }
      const discountPercentage = agreement?.discountPercentage;
      formMethods.setValue(
        "discountPercentage",
        discountPercentage ? l10n.formatNumber(discountPercentage) : ""
      );
      formMethods.setValue(
        "description",
        agreement?.name || product.name || ""
      );
      formMethods.setValue(
        "unitId",
        agreement?.unitId || product.unitId || null
      );
      formMethods.setValue(
        "quantity",
        l10n.formatNumber(agreement?.unitNumber || 1)
      );
    },
    [optionByValue, products, l10n, formMethods]
  );

  // When editing an existing line, preselect the option matching its product.
  // Lines don't record which agreement was applied, so only plain-product options
  // can be matched back; a product that exists solely through agreements stays
  // unselected until the user picks one.
  useEffect(() => {
    if (selectedOptionValue !== undefined) {
      return;
    }
    const initialProductId = getValues("productId");
    if (!initialProductId) {
      return;
    }
    const match = options.find(
      (o) => o.productId === initialProductId && o.agreement === null
    );
    if (match) {
      setSelectedOptionValue(match.value);
    }
  }, [options, selectedOptionValue, getValues]);

  const selectedOption = selectedOptionValue
    ? optionByValue.get(selectedOptionValue)
    : undefined;

  const product = useMemo(
    () =>
      selectedOption
        ? products.find((x) => x.id === selectedOption.productId)
        : null,
    [selectedOption, products]
  );

  const unitForSelectedProduct = useMemo((): string => {
    if (!selectedOption) {
      return "";
    }
    const product = products.find((x) => x.id === selectedOption.productId);
    if (!product) {
      return "";
    }
    const { agreement } = selectedOption;
    const unit = agreement?.unitId
      ? units?.find((u) => u.id === agreement.unitId)
      : units?.find((u) => u.id === product.unitId);
    return unit?.name || "";
  }, [selectedOption, products, units]);

  const unitOptions = useMemo(
    () =>
      (units || []).map((unit) => ({
        value: unit.id,
        label: isPowerOffice
          ? t(`types:powerOfficeProductUnits.${unit.name}` as any)
          : unit.name,
      })),
    [units, isPowerOffice, t]
  );

  return (
    <FormProvider {...formMethods}>
      <form role="row form" className="income-grid__row" onSubmit={submitRow}>
        {!skipInvoiceLineSpecificCells && !noCheckboxes && (
          <div className="add-invoice-line-row__cell" role="gridcell">
            {/* selected */}
          </div>
        )}
        <div className="add-invoice-line-row__cell" role="gridcell">
          <DoraSelect
            fullWidth
            autoselect
            aria-labelledby="income-grid-heading-product"
            value={selectedOptionValue}
            onChange={handleProductOptionChange}
            customRenderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                name="productId"
                id={"productId"}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Code",
                }}
              >
                {product?.name}
              </TextField>
            )}
            options={options}
            selectableOptions={selectableOptions}
            autofocus
          />
        </div>
        <div className="add-invoice-line-row__cell" role="gridcell">
          {descriptionAutocompleteEnabled ? (
            <UFInvoiceLineDescriptionAutocomplete
              name="description"
              productId={selectedProductId}
              control={control}
              aria-label={t("invoicing:invoiceLine.description")}
            />
          ) : (
            <UFDoraTextInput
              register={register("description")}
              aria-label={t("invoicing:invoiceLine.description")}
            />
          )}
        </div>
        <div className="add-invoice-line-row__cell" role="gridcell">
          <UFDoraTextInput
            aria-label={t("invoicing:invoiceLine.quantity")}
            register={register("quantity")}
            numberOnly
            {...inputValidationProps(quantityValidationId, "quantity")}
            error={!!errors["quantity"]}
          />
        </div>
        <div className="add-invoice-line-row__cell" role="gridcell">
          {isTripletex ? (
            <div>{unitForSelectedProduct}</div>
          ) : (
            <UFDoraSelect
              control={control}
              fullWidth
              autoselect
              name="unitId"
              options={unitOptions}
              aria-labelledby="income-grid-heading-unit"
            />
          )}
        </div>
        <div className="add-invoice-line-row__cell" role="gridcell">
          <UFDoraTextInput
            aria-label={t("invoicing:invoiceLine.amount")}
            register={register("amount")}
            numberOnly
            error={!!errors["amount"]}
            {...inputValidationProps(amountValidationId, "amount")}
          />
        </div>
        {!skipInvoiceLineSpecificCells && (
          <div className="add-invoice-line-row__cell" role="gridcell"></div>
        )}
        <div className="add-invoice-line-row__cell" role="gridcell">
          <UFDoraTextInput
            style={{ maxWidth: "4.5rem" }}
            aria-label={t("invoicing:invoiceLine.discountPercentage")}
            register={register("discountPercentage")}
            numberOnly
            error={!!errors["discountPercentage"]}
            {...inputValidationProps(amountValidationId, "discountPercentage")}
          />
        </div>
        {!skipInvoiceLineSpecificCells && (
          <div className="add-invoice-line-row__cell" role="gridcell">
            <div role="gridcell" className="flex items-center">
              {props.createdDate && l10n.formatDate(props.createdDate)}
            </div>
          </div>
        )}
        <div className="add-invoice-line-row__cell justify-end" role="gridcell">
          <Total />
        </div>
        {!skipInvoiceLineSpecificCells && showStatusColumn && (
          <div className="add-invoice-line-row__cell" role="gridcell" />
        )}
        <div className="add-invoice-line-row__last-cell" role="gridcell">
          <DoraButton
            variant="primary"
            type="button"
            className={"add-invoice-line-last-cell__button"}
            title={t("components:buttonLabels.cancel")}
            aria-label={t("components:buttonLabels.cancel")}
            onClick={onCancel}
            disabled={isSubmittingRow}
          >
            <FontAwesomeIcon icon={faTimes} />
          </DoraButton>
          <DoraButton
            variant="primary"
            className={"add-invoice-line-last-cell__button"}
            type="submit"
            title={t("components:buttonLabels.save")}
            aria-label={t("components:buttonLabels.save")}
            disabled={isSubmittingRow}
          >
            <FontAwesomeIcon icon={faCheck} />
          </DoraButton>
        </div>
      </form>

      {errors["quantity"] || errors["amount"] || extraError ? (
        <div className="income-handler-row__errors">
          {errors["quantity"] ? (
            <ValidationError id={quantityValidationId} field="quantity" />
          ) : null}
          {errors["amount"] ? (
            <ValidationError id={amountValidationId} field="amount" />
          ) : null}
          {extraError && <div>{extraError}</div>}
        </div>
      ) : null}
    </FormProvider>
  );
};

export default InvoiceLineRowForm;
